import { useCallback, useEffect, useRef, useState } from "react";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { useModel } from "./contexts/ModelContext";
import { useAnalytics } from "./contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "./contexts/FlowContext";
import { useFlowState } from "@/hooks/useFlowState";
import { Flows } from "@/types/flows";
import { useGetPredictions, GetPredictionsForFileParams } from "./hooks/useGetPredictions";
import { useMedia } from "./contexts/MediaContext";
import { useCurrentWorkflow, useCurrentWorkspaceApiKey } from "@/store";
import { MediaItem, MediaItemStatus, MediaItemUuid, MediaItemFile } from "@/types/media";
import { useMediaUtils } from "./hooks/useMediaUtils";
import { UploadLimitErrorType } from "@/constants/media";
import { validateUploadLimits as validateUploadLimitsUtil } from "@/utils/mediaValidation";

export const useTestWithMoreFiles = () => {
    const { track } = useAnalytics();
    const { task } = useFlow();
    const { flow, step } = useFlowState();
    const { addMediaItems, completedMediaItems } = useMedia();
    const { updateMediaItem } = useMediaUtils();
    const { setIsTestingNewFiles, setTestingProgress } = useModel();
    const { getPredictionsForFile } = useGetPredictions();
    const [testFileMediaItemIds, setTestFileMediaItemIds] = useState<MediaItemUuid[]>([]);
    const [uploadLimitError, setUploadLimitError] = useState<UploadLimitErrorType | null>(null);
    const [isOutOfCredits, setIsOutOfCredits] = useState(false);
    const testFilesRef = useRef<MediaItemUuid[]>([]);
    const isTestingRef = useRef(false);
    const workflow = useCurrentWorkflow();
    const apiKey = useCurrentWorkspaceApiKey();

    const uploadAndProcessFiles = useCallback(
        async (files: MediaItemFile[]) => {
            const createdMediaItems = await addMediaItems(files);
            const mediaItemIds = createdMediaItems.map((i) => i.id);

            setTestFileMediaItemIds(mediaItemIds);
            setTestingProgress(25);

            // Return the created media item IDs so we can track them
            return createdMediaItems;
        },
        [addMediaItems, setTestingProgress]
    );

    const validateUploadLimits = useCallback(
        (files: MediaItemFile[]): UploadLimitErrorType | null => {
            // Only apply limits in deploy, share, and build flows
            // Note: BUILD flow applies limits because it's used on the Review step
            // where users can "Test with more files"
            if (flow !== Flows.DEPLOY && flow !== Flows.SHARE && flow !== Flows.BUILD) {
                return null;
            }

            return validateUploadLimitsUtil(files);
        },
        [flow]
    );

    const testWithMoreFiles = useCallback(
        async (files: MediaItemFile[], onFinally?: () => void) => {
            if (!files || files.length === 0 || testFilesRef.current.length !== 0) {
                return;
            }

            // Validate upload limits
            const limitError = validateUploadLimits(files);
            if (limitError) {
                setUploadLimitError(limitError);
                return; // Keep modal open to show error
            }

            // Clear any previous upload limit errors
            setUploadLimitError(null);

            // Validation passed - close modal immediately
            onFinally?.();

            setIsTestingNewFiles(true);
            setTestingProgress(0);
            setTestFileMediaItemIds([]);

            try {
                const createdMediaItems = await uploadAndProcessFiles(files);
                testFilesRef.current = createdMediaItems.map((i) => i.id);
                return createdMediaItems;
            } catch (error) {
                console.error("Error uploading files:", error);
                setIsTestingNewFiles(false);
                setTestingProgress(0);
                setTestFileMediaItemIds([]);
            }
        },
        [setIsTestingNewFiles, setTestingProgress, uploadAndProcessFiles, validateUploadLimits]
    );

    const getPredictionsForTestFiles = useCallback(async () => {
        // Skip if no test files are being tracked
        if (testFileMediaItemIds.length === 0) {
            return;
        }

        // Find the current test media items from completed upload media items
        const currentTestFileMediaItems = testFileMediaItemIds
            .map((id) => completedMediaItems.find((u) => u.id === id))
            .filter(Boolean) as MediaItem[];

        // Check if all test files have completed uploading
        if (
            currentTestFileMediaItems.length === testFileMediaItemIds.length &&
            currentTestFileMediaItems.length > 0
        ) {
            if (isTestingRef.current) {
                // already running inference on these files. exit
                return;
            }

            isTestingRef.current = true;

            try {
                // Continue with prediction processing in the background
                const totalFiles = currentTestFileMediaItems.length;
                let completedFiles = 0;

                const updateProgress = () => {
                    const uploadProgress = 50; // Uploads are done
                    const predictionProgress = (completedFiles / totalFiles) * 50;
                    setTestingProgress(Math.round(uploadProgress + predictionProgress));
                };

                // Process predictions for each test file
                const predictionPromises = currentTestFileMediaItems.map(async (mediaItem) => {
                    try {
                        // Track analytics
                        track({
                            event: AnalyticsFlowEvent.MODEL_TESTED,
                            data: {
                                task,
                                file_uuid: mediaItem.id,
                                step,
                                flow,
                                type: mediaItem.isVideo ? "video" : "image",
                                name: mediaItem.file.name,
                                size: mediaItem.file.size,
                                size_mb: mediaItem.file.size / 1024 / 1024,
                                format: mediaItem.file.type,
                                metadata: mediaItem.metadata?.serverMediaDimensions
                            }
                        });

                        // Create abort controller for this operation
                        const abortController = new AbortController();

                        // Generate predictions using the streaming API
                        // Small delay to ensure React state is properly synchronized
                        await new Promise((resolve) => setTimeout(resolve, 1));

                        const getPredictionsPayload: GetPredictionsForFileParams = {
                            mediaItem,
                            abortController,
                            useInferenceQueue: true // Ensure timeline loading overlay shows
                        };

                        // If we're in the deploy flow and have a workflow, use workflow inference
                        // Otherwise fall back to default inference type (OWLVIT)
                        if (flow === Flows.DEPLOY && workflow?.id && apiKey) {
                            getPredictionsPayload.inferenceType = InferenceTypes.WORKFLOW;
                        } else if (flow === Flows.SHARE) {
                            getPredictionsPayload.inferenceType = InferenceTypes.WORKFLOW_PUBLIC;
                        } else if (flow === Flows.DEPLOY) {
                            // Fall back to default inference type
                        }

                        await getPredictionsForFile(getPredictionsPayload);

                        completedFiles++;
                        updateProgress();
                    } catch (error: any) {
                        console.error(`Failed to process ${mediaItem.file.name}:`, error);

                        // Check if this is a 402 Payment Required error
                        if (error?.statusCode === 402 || error?.message?.includes("402")) {
                            setIsOutOfCredits(true);
                            // Stop processing further files
                            throw error;
                        }

                        completedFiles++;
                        updateProgress();
                    }
                });

                // Wait for all predictions to complete
                await Promise.allSettled(predictionPromises);

                // Final progress update
                setTestingProgress(100);
            } catch (error) {
                console.error("Error triggering upload queue tests:", error);
            } finally {
                setIsTestingNewFiles(false);
                setTestingProgress(0);
                isTestingRef.current = false;
                setTestFileMediaItemIds([]);
                testFilesRef.current = [];
            }
        }
    }, [
        testFileMediaItemIds,
        completedMediaItems,
        getPredictionsForFile,
        task,
        track,
        setIsTestingNewFiles,
        setTestingProgress,
        step,
        flow,
        workflow,
        apiKey
    ]);

    useEffect(() => {
        getPredictionsForTestFiles();
    }, [getPredictionsForTestFiles]);

    return {
        testWithMoreFiles,
        uploadLimitError,
        clearUploadLimitError: () => setUploadLimitError(null),
        isOutOfCredits,
        clearOutOfCreditsError: () => setIsOutOfCredits(false)
    };
};
